import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import { useDispatch } from "react-redux";

import { useCurrentUser } from "@/hooks/useCurrentUser";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import {
  agentAccessService,
  type AgentAccessList,
  type AgentAccessUser,
  type UpdateAgentAccessRequest,
} from "@/services/agentAccess";
import { authService } from "@/services/auth";
import { getOrganizationSettingsErrorMessage } from "@/services/organizationSettings";
import { setUser } from "@/store/slices/authSlice";
import type { AppDispatch } from "@/store/store";
import type { UserRoles } from "@/types/auth";
import { formatDateTime } from "@/utils/dateUtils";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

export const AGENT_ACCESS_QUERY_KEY = ["agentAccess"] as const;

const ROLE_LABELS: Record<UserRoles, string> = {
  admin: "Administrator",
  admin_support: "Admin Support",
  underwriter: "Underwriter",
  broker: "Broker",
};

/**
 * Which of the organization's users may use the AI assistant: a switch per user, and select-all
 * with Give/Remove access for many at once. Shown to the organization's admins only, while the
 * organization has the assistant on (``capabilities.manage_agent_access``). Removing the
 * admin's own access asks first: it takes the assistant and its settings away from them. A phone
 * gets a list instead of the table: each user's switch stays on screen, with no sideways scroll.
 */
const AiAccessSection = () => {
  const queryClient = useQueryClient();
  const dispatch = useDispatch<AppDispatch>();
  const { user: me } = useCurrentUser();
  // Below Tailwind's sm. Where matchMedia is missing (tests) it is the table.
  const phone = useMediaQuery("(max-width: 639px)");
  const [selected, setSelected] = useState<Set<number>>(new Set());
  // A change that removes the admin's own access, waiting for them to confirm it.
  const [confirming, setConfirming] = useState<UpdateAgentAccessRequest | null>(null);

  const query = useQuery({ queryKey: AGENT_ACCESS_QUERY_KEY, queryFn: agentAccessService.list });
  const users = query.data?.users ?? [];
  // Only users still listed count as selected (one may have left since it was ticked).
  const listed = new Set(users.map((u) => u.id));
  const selectedIds = [...selected].filter((id) => listed.has(id));

  const mutation = useMutation({
    mutationFn: (payload: UpdateAgentAccessRequest) => agentAccessService.update(payload),
    onSuccess: (data: AgentAccessList, payload) => {
      queryClient.setQueryData(AGENT_ACCESS_QUERY_KEY, data);
      setSelected(new Set());
      // An admin who changed their own access: the assistant's entry points follow /me. The
      // change itself is saved, so a failed reload is left to the next page load.
      if (me && payload.user_ids.includes(me.id)) {
        authService
          .getCurrentUser()
          .then((user) => dispatch(setUser(user)))
          .catch(() => undefined);
      }
    },
  });

  const allSelected = users.length > 0 && selectedIds.length === users.length;
  const someSelected = selectedIds.length > 0 && !allSelected;
  const busy = query.isLoading || mutation.isPending;

  const toggleAll = (on: boolean) => setSelected(new Set(on ? users.map((u) => u.id) : []));
  const toggleOne = (id: number, on: boolean) =>
    setSelected((previous) => {
      const next = new Set(previous);
      if (on) next.add(id);
      else next.delete(id);
      return next;
    });
  const setAccess = (userIds: number[], on: boolean) => {
    const payload = { user_ids: userIds, agent_access: on };
    if (!on && me && userIds.includes(me.id)) setConfirming(payload);
    else mutation.mutate(payload);
  };

  const placeholder = query.isLoading ? (
    <Loader2 className="mx-auto h-4 w-4 animate-spin" />
  ) : (
    "No users yet."
  );
  const selectAll = (
    <Checkbox
      aria-label="Select all users"
      checked={allSelected ? true : someSelected ? "indeterminate" : false}
      onCheckedChange={(state) => toggleAll(state === true)}
      disabled={busy || users.length === 0}
    />
  );
  const selectOne = (user: AgentAccessUser) => (
    <Checkbox
      aria-label={`Select ${user.email}`}
      checked={selected.has(user.id)}
      onCheckedChange={(state) => toggleOne(user.id, state === true)}
      disabled={busy}
    />
  );
  const accessSwitch = (user: AgentAccessUser) => (
    <Switch
      aria-label={`AI access for ${user.email}`}
      checked={user.agent_access}
      onCheckedChange={(on) => setAccess([user.id], on)}
      disabled={busy}
    />
  );
  const changedNote = (user: AgentAccessUser) =>
    user.agent_access_changed_at && (
      <div className="mt-1 text-xs text-muted-foreground">
        {changedLine(user.agent_access_changed_by, user.agent_access_changed_at)}
      </div>
    );

  return (
    <Card className="overflow-hidden p-0">
      <div className="flex flex-wrap items-center justify-between gap-2 border-b px-4 py-3">
        <h2 className="text-sm font-semibold">AI access</h2>
        {selectedIds.length > 0 && (
          <div className="flex items-center gap-2">
            <span className="text-xs text-muted-foreground">{selectedIds.length} selected</span>
            <Button size="sm" disabled={busy} onClick={() => setAccess(selectedIds, true)}>
              Give access
            </Button>
            <Button
              size="sm"
              variant="outline"
              disabled={busy}
              onClick={() => setAccess(selectedIds, false)}
            >
              Remove access
            </Button>
          </div>
        )}
      </div>

      {(query.isError || mutation.isError) && (
        <Alert variant="destructive" className="m-4 w-auto">
          <AlertDescription>
            {getOrganizationSettingsErrorMessage(mutation.error ?? query.error)}
          </AlertDescription>
        </Alert>
      )}

      {phone ? (
        <ul className="divide-y">
          <li>
            {/* The whole row toggles: its text is the box's label, not a second control. */}
            <label className="flex cursor-pointer items-center gap-3 px-4 py-2 text-xs text-muted-foreground">
              {selectAll}
              <span aria-hidden="true">Select all</span>
            </label>
          </li>
          {query.isLoading || users.length === 0 ? (
            <li className="py-6 text-center text-sm text-muted-foreground">{placeholder}</li>
          ) : (
            users.map((user) => (
              <li
                key={user.id}
                className="flex items-start gap-3 px-4 py-3 data-[state=selected]:bg-muted"
                data-state={selected.has(user.id) ? "selected" : undefined}
              >
                <div className="pt-0.5">{selectOne(user)}</div>
                <div className="min-w-0 flex-1 break-words">
                  <div className="text-sm font-medium">{user.email}</div>
                  <div className="text-xs text-muted-foreground">
                    {[ROLE_LABELS[user.role] ?? user.role, user.company_name]
                      .filter(Boolean)
                      .join(" · ")}
                  </div>
                  {changedNote(user)}
                </div>
                {accessSwitch(user)}
              </li>
            ))
          )}
        </ul>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-10">{selectAll}</TableHead>
              <TableHead>User</TableHead>
              <TableHead>Role</TableHead>
              <TableHead className="text-right">AI access</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {query.isLoading || users.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} className="py-6 text-center text-sm text-muted-foreground">
                  {placeholder}
                </TableCell>
              </TableRow>
            ) : (
              users.map((user) => (
                <TableRow key={user.id} data-state={selected.has(user.id) ? "selected" : undefined}>
                  <TableCell>{selectOne(user)}</TableCell>
                  <TableCell>
                    <div className="font-medium">{user.email}</div>
                    {user.company_name && (
                      <div className="text-xs text-muted-foreground">{user.company_name}</div>
                    )}
                  </TableCell>
                  <TableCell>{ROLE_LABELS[user.role] ?? user.role}</TableCell>
                  <TableCell className="text-right">
                    {accessSwitch(user)}
                    {changedNote(user)}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      )}

      <AlertDialog open={confirming !== null} onOpenChange={(open) => !open && setConfirming(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove your own AI access?</AlertDialogTitle>
            <AlertDialogDescription>
              You will no longer be able to use the AI assistant or change its organization
              settings. You can still give access back here, to yourself and others.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (confirming) mutation.mutate(confirming);
                setConfirming(null);
              }}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Remove access
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
};

/** Who last changed a user's access, and when ("Steddy" for staff outside the organization). */
function changedLine(by: string | null, at: string): string {
  return by ? `Changed by ${by}, ${formatDateTime(at)}` : `Changed ${formatDateTime(at)}`;
}

export default AiAccessSection;
